import { z } from "zod";

const finite = z.number().finite();
const degrees = finite;

const ElementsSchema = z
  .object({
    epochJd: finite,
    frame: z.literal("ecliptic"),
    a: finite.positive(),
    e: finite.min(0).lt(1),
    i: degrees,
    node: degrees,
    periodDays: finite.positive(),
    omega: degrees.optional(),
    m0: degrees.optional(),
    L0: degrees.optional(),
    varpi: degrees.optional(),
    rates: z
      .object({ a: finite, e: finite, i: finite, L: finite, varpi: finite, node: finite })
      .optional(),
    correction: z.object({ b: finite, c: finite, s: finite, f: finite }).optional(),
    /** JPL's own inclination for moons whose JPL frame is not the ecliptic; display only. */
    inclination: z.object({ deg: degrees, plane: z.enum(["equator", "laplace"]) }).optional(),
  })
  .refine(
    (el) =>
      (el.omega !== undefined && el.m0 !== undefined) !==
      (el.L0 !== undefined && el.varpi !== undefined),
    {
      message: "elements need either omega+m0 or L0+varpi, not both and not neither",
    },
  );

const TexturesSchema = z.object({
  map: z.string(),
  map8k: z.string().optional(),
  night: z.string().optional(),
  clouds: z.string().optional(),
  ring: z.string().optional(),
  normal: z.string().optional(),
  specular: z.string().optional(),
});

const PhysicalSchema = z.object({
  radiusKm: finite.positive(),
  /** True when radiusKm is only a placeholder to draw a moon JPL has no measured size for. */
  radiusUnknown: z.literal(true).optional(),
  /** Gravitational parameter in km³/s²; null when unmeasured. */
  gm: finite.positive().nullable(),
  densityGcm3: finite.positive().nullable(),
  /** Mean temperature in °C: the surface, or the 1 bar level for the giant planets. */
  meanTempC: finite.nullable(),
  rotationPeriodHours: finite.nullable(),
  axialTiltDeg: finite.nullable(),
  /** North rotation pole, degrees in ICRF/J2000 equatorial coordinates. */
  pole: z.object({ ra: degrees, dec: finite.min(-90).max(90) }).optional(),
  color: z.string().regex(/^#[0-9a-f]{6}$/i),
  textures: TexturesSchema.optional(),
});

const BodySchema = z.object({
  id: z.string().regex(/^[a-z0-9-]+$/),
  name: z.string().min(1),
  kind: z.enum(["star", "planet", "dwarf", "moon"]),
  parent: z.string().nullable(),
  orbit: ElementsSchema.nullable(),
  physical: PhysicalSchema,
  tier: z.enum(["textured", "simple", "point"]),
});

const CatalogueSchema = z.array(BodySchema).superRefine((bodies, ctx) => {
  const seen = new Set<string>();
  let roots = 0;
  for (const body of bodies) {
    if (seen.has(body.id)) ctx.addIssue({ code: "custom", message: `duplicate id "${body.id}"` });
    if (body.parent === null) {
      roots += 1;
    } else {
      if (body.orbit === null)
        ctx.addIssue({ code: "custom", message: `"${body.id}" needs an orbit` });
      const known = bodies.some((b) => b.id === body.parent);
      if (!known)
        ctx.addIssue({
          code: "custom",
          message: `parent "${body.parent}" of "${body.id}" does not exist`,
        });
      else if (!seen.has(body.parent))
        ctx.addIssue({
          code: "custom",
          message: `parent "${body.parent}" must precede "${body.id}"`,
        });
    }
    seen.add(body.id);
  }
  if (roots !== 1)
    ctx.addIssue({ code: "custom", message: `catalogue needs exactly one root, found ${roots}` });
});

export type Elements = z.infer<typeof ElementsSchema>;
export type Textures = z.infer<typeof TexturesSchema>;
export type Body = z.infer<typeof BodySchema>;
export type Catalogue = z.infer<typeof CatalogueSchema>;
export type Tier = Body["tier"];

export function parseCatalogue(raw: unknown): Catalogue {
  const result = CatalogueSchema.safeParse(raw);
  if (result.success) return result.data;
  const lines = result.error.issues.map((i) => `${i.path.join(".") || "<root>"}: ${i.message}`);
  throw new Error(`catalogue invalid:\n${lines.join("\n")}`);
}
